import React from 'react';
import { useAvContext } from '@context/AvContextProvider';
import {
  AddOperandButton,
  boxWidth,
  cleanValueFromExpression,
  Filter,
  filterOperatorOptions,
  getEmptyFilterExpression,
  isIngressExp,
  OperatorType,
  RelationsProps,
  shouldRenderWithFieldFilterBox,
  StringConditionType,
} from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { emptyObject } from '@fabric-ui/utils';
import { Box, useTheme } from '@mui/material';
import FieldFilterBox, { FieldFilterBoxProps } from './FieldFilterBox';

interface RenderOperatorProps {
  filter: Filter;
  setFilter: any;
  fields: any[];
  isLoadingFields?: boolean;
  isVertical: boolean;
  size: 'xSmall' | 'small';
  defaultField: string | null;
  rootOperator: 'or' | 'and';
  level?: number;
  onToggleLogicalOperators: () => void;
  canAddNewFields?: boolean;
  canSelectFieldType?: boolean;
  supportParser?: boolean;
  autocompleteFilter?: Date[];
  canDeleteLast?: boolean;
  operatorOptions?: typeof filterOperatorOptions;
  ingressModeProps?: FieldFilterBoxProps['ingressModeProps'];
  relationsProps: RelationsProps;
  disableMessage?: string;
  isOverExpressionsLimit?: boolean;
  fieldSelectionMessage?: string;
  fieldOnly?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  fieldsListAdornment?: React.ReactNode;
  hasListTab?: boolean;
  activeProjName?: string;
  icon?: React.ReactNode;
  isHistorical?: boolean;
}

const RenderOperator: React.FC<RenderOperatorProps> = ({
  filter,
  setFilter,
  rootOperator,
  size,
  fields,
  isLoadingFields,
  isVertical,
  level = 0,
  defaultField,
  onToggleLogicalOperators,
  canAddNewFields,
  canSelectFieldType,
  supportParser,
  autocompleteFilter,
  canDeleteLast,
  operatorOptions,
  ingressModeProps,
  relationsProps = emptyObject as RelationsProps,
  disableMessage,
  isOverExpressionsLimit,
  fieldSelectionMessage,
  fieldOnly = false,
  disabled = false,
  readOnly = false,
  fieldsListAdornment,
  hasListTab = false,
  activeProjName,
  icon,
  isHistorical,
}) => {
  const { palette } = useTheme();
  const {
    accountEntities: { fieldMap },
  } = useAvContext();

  const operatorColors = {
    and: palette.colors.primary[600],
    or: palette.colors.complementary[600],
  };

  const commonProps = {
    size,
    isVertical,
    fields,
    isLoadingFields,
    canAddNewFields,
    canSelectFieldType,
    supportParser,
    operatorOptions,
    relationsProps,
    disableMessage,
    isOverExpressionsLimit,
    fieldSelectionMessage,
    fieldOnly,
    disabled,
    fieldsListAdornment,
    hasListTab,
    activeProjName,
    isHistorical,
  };

  const operandsList = filter[rootOperator]?.operands;
  return operandsList?.map((innerFilter, index) => {
    const oppositeOperator = rootOperator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;
    const setInnerFilter = value => {
      const operands = operandsList.map((v, i) => (i === index ? value : v));
      setFilter({ [rootOperator]: { operands } });
    };

    const onDelete = () => {
      const operands = operandsList.filter((v, i) => i !== index);
      if (operands.length === 0 && level === 0) {
        setFilter(
          canDeleteLast
            ? undefined
            : relationsProps.defaultEmptyFilter
              ? { and: { operands: [relationsProps.defaultEmptyFilter] } }
              : getEmptyFilterExpression(defaultField, supportParser ? StringConditionType.EQUALS : undefined)
        );
      } else if (operands.length === 1 && (level !== 0 || !operands[0].expression)) {
        setFilter(operands[0]);
      } else {
        setFilter({ [rootOperator]: { operands } });
      }
    };

    const getOperands = () => {
      const { operands } = filter[rootOperator]!;
      // @ts-ignore
      const newItem = isIngressExp(operands[index]) ? operands[index] : cleanValueFromExpression(operands[index], fieldMap);

      if (level === 0) {
        return operands.with(index, { [oppositeOperator]: { operands: [operands[index], newItem] } });
      }
      return operands.toSpliced(index + 1, 0, newItem);
    };

    const addLogicalExpression = () => setFilter({ [rootOperator]: { operands: getOperands() } });
    const isIngressExpression = isIngressExp(innerFilter); // TODO - add projection as prop to the component?

    return (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{ borderLeft: `2px solid ${operatorColors[rootOperator]}`, ml: level * 2 }}>
        {index > 0 && operandsList.length > index && (
          <Box
            sx={{
              ...flex.itemsStart,
              my: '-4px',
              ml: isVertical ? `${boxWidth / 2}px` : `${boxWidth / 4}px`,
            }}>
            <AddOperandButton
              style={{ translate: '-50%' }}
              size={size}
              operator={rootOperator}
              onClick={onToggleLogicalOperators}
              disabled={disabled}
            />
          </Box>
        )}
        {shouldRenderWithFieldFilterBox(innerFilter, hasListTab) ? (
          <Box sx={{ ...flex.justifyStartCenter, gap: 1 }}>
            <FieldFilterBox
              {...commonProps}
              autocompleteFilter={autocompleteFilter}
              rootOperator={rootOperator}
              setFilter={setInnerFilter}
              level={level}
              filter={innerFilter}
              onDelete={onDelete}
              addLogicalExpression={fieldOnly || operandsList.length === 1 ? undefined : addLogicalExpression}
              canSelectFieldType={canSelectFieldType}
              ingressModeProps={ingressModeProps}
              isIngressExp={isIngressExpression}
              defaultField={defaultField}
              readOnly={readOnly}
            />
            {icon}
          </Box>
        ) : (
          <RenderOperator
            {...commonProps}
            autocompleteFilter={autocompleteFilter}
            defaultField={defaultField}
            filter={innerFilter}
            setFilter={setInnerFilter}
            rootOperator={oppositeOperator}
            level={level + 1}
            onToggleLogicalOperators={onToggleLogicalOperators}
            ingressModeProps={ingressModeProps}
            icon={icon}
          />
        )}
      </Box>
    );
  });
};

export default RenderOperator;
